/**
 * Risk Assessment Modal
 *
 * Displays detailed risk breakdown for a container.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/lib/api-client';
import { Modal, ModalOverlay, Dialog } from '@/components/application/modals/modal';
import { Button } from '@/components/base/buttons/button';
import { Badge } from '@/components/base/badges/badges';
import { ProgressBar } from '@/components/base/progress-indicators/progress-indicators';
import {
    AlertCircle,
    TrendUp01,
    File06,
    Clock,
    Truck01,
    Thermometer01,
    Building02,
    CheckCircle,
    XCircle,
    Download01,
} from '@untitledui/icons';
import { downloadRiskAssessmentPDF } from './risk-assessment-pdf';

interface RiskAssessmentModalProps {
    containerId: string;
    containerNumber: string;
    isOpen: boolean;
    onClose: () => void;
}

interface RiskFactor {
    source: 'container_issue' | 'document_issue';
    issue_id: string;
    type: string;
    severity: string;
    component: string;
    weight: number;
    title: string;
}

interface RiskAssessmentResponse {
    success: boolean;
    container_id: string;
    container_number: string;
    current_phase: string;
    risk_assessment: {
        overall_score: number;
        risk_level: 'minimal' | 'low' | 'medium' | 'high' | 'critical';
        component_scores: {
            document: number;
            timeline: number;
            carrier: number;
            temperature: number;
            customs: number;
        };
        active_issues_count: number;
    };
    risk_factors: RiskFactor[];
    recommendations?: string[];
    historical_comparison?: {
        average_risk: number;
        percentile: number;
        trend?: string;
    };
}

export function RiskAssessmentModal({
    containerId,
    containerNumber,
    isOpen,
    onClose,
}: RiskAssessmentModalProps) {
    const { t } = useTranslation('pages:containers');
    const [isDownloading, setIsDownloading] = useState(false);

    const { data, isLoading, error } = useQuery({
        queryKey: ['container-risk', containerId],
        queryFn: async () => {
            return apiGet<RiskAssessmentResponse>(`/container-issues/containers/${containerId}/risk`);
        },
        enabled: isOpen && !!containerId,
    });

    // Extract nested data for easier access
    const riskData = data?.risk_assessment;
    const riskLevel = riskData?.risk_level || 'low';

    const getRiskColor = (level: string) => {
        switch (level) {
            case 'minimal':
            case 'low':
                return 'success';
            case 'medium':
                return 'warning';
            case 'high':
            case 'critical':
                return 'error';
            default:
                return 'gray';
        }
    };

    const componentIcons = {
        document: File06,
        timeline: Clock,
        carrier: Truck01,
        temperature: Thermometer01,
        customs: Building02,
    };

    const componentLabels = {
        document: 'Document Risk',
        timeline: 'Timeline Risk',
        carrier: 'Carrier Risk',
        temperature: 'Temperature Risk',
        customs: 'Customs Risk',
    };

    const handleDownloadPDF = async () => {
        if (!data) return;
        setIsDownloading(true);
        try {
            await downloadRiskAssessmentPDF(data, containerNumber);
        } finally {
            setIsDownloading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <ModalOverlay isOpen={isOpen} onOpenChange={(open) => !open && onClose()}>
            <Modal>
                <Dialog>
                    <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-2xl w-full">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h2 className="text-xl font-semibold text-primary">
                                    {t('detail.actions.riskAssessment', 'Risk Assessment')}
                                </h2>
                                <p className="text-sm text-tertiary mt-1">
                                    {t('detail.actions.riskAssessmentDesc', 'Detailed risk analysis for container {{number}}', { number: containerNumber })}
                                </p>
                            </div>
                            <button
                                onClick={onClose}
                                className="text-tertiary hover:text-primary"
                            >
                                <XCircle className="size-5" />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="min-h-[300px]">
                            {isLoading && (
                                <div className="flex items-center justify-center py-12">
                                    <p className="text-tertiary">{t('detail.loading', 'Loading...')}</p>
                                </div>
                            )}

                            {/* Show placeholder when error or no data */}
                            {(error || (!isLoading && !data)) && (
                                <div className="flex flex-col items-center justify-center py-12 text-center">
                                    <div className="rounded-full bg-secondary/50 p-4 mb-4">
                                        <TrendUp01 className="h-8 w-8 text-tertiary" />
                                    </div>
                                    <h3 className="text-lg font-medium text-primary mb-2">
                                        {t('detail.actions.noRiskData', 'No Risk Data Available')}
                                    </h3>
                                    <p className="text-sm text-tertiary max-w-sm">
                                        {t('detail.actions.noRiskDataDesc', 'Risk assessment data is not yet available for this container. It will be calculated when there are activities or issues to analyze.')}
                                    </p>
                                </div>
                            )}

                            {data && riskData && (
                                <div className="space-y-6">
                                    {/* Overall Score */}
                                    <div className={`p-4 rounded-lg ring-1 ${
                                        riskLevel === 'minimal' || riskLevel === 'low'
                                            ? 'bg-success-primary ring-success-500/30'
                                            : riskLevel === 'medium'
                                            ? 'bg-warning-primary ring-warning-500/30'
                                            : 'bg-error-primary ring-error-500/30'
                                    }`}>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <TrendUp01 className={`h-8 w-8 ${
                                                    riskLevel === 'minimal' || riskLevel === 'low'
                                                        ? 'text-fg-success-primary'
                                                        : riskLevel === 'medium'
                                                        ? 'text-fg-warning-primary'
                                                        : 'text-fg-error-primary'
                                                }`} />
                                                <div>
                                                    <h3 className="text-lg font-semibold text-primary">
                                                        {t('detail.actions.overallRisk', 'Overall Risk Score')}
                                                    </h3>
                                                    <p className="text-sm text-secondary">
                                                        {riskData.active_issues_count} active issues
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-3xl font-bold text-primary">
                                                    {((riskData.overall_score || 0) * 100).toFixed(0)}%
                                                </div>
                                                <Badge color={getRiskColor(riskLevel)} size="md">
                                                    {riskLevel.charAt(0).toUpperCase() + riskLevel.slice(1)}
                                                </Badge>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Component Breakdown */}
                                    {riskData.component_scores && Object.keys(riskData.component_scores).length > 0 && (
                                    <div>
                                        <h4 className="text-sm font-medium text-primary mb-3">
                                            {t('detail.actions.componentBreakdown', 'Component Breakdown')}
                                        </h4>
                                        <div className="space-y-3">
                                            {Object.entries(riskData.component_scores).map(([key, score]) => {
                                                const Icon = componentIcons[key as keyof typeof componentIcons];
                                                const label = componentLabels[key as keyof typeof componentLabels];
                                                return (
                                                    <div key={key} className="flex items-center gap-3">
                                                        <Icon className="h-5 w-5 text-tertiary shrink-0" />
                                                        <div className="flex-1">
                                                            <div className="flex items-center justify-between mb-1">
                                                                <span className="text-sm text-primary">{label}</span>
                                                                <span className="text-sm font-medium text-primary">
                                                                    {(score * 100).toFixed(0)}%
                                                                </span>
                                                            </div>
                                                            <ProgressBar
                                                                value={score * 100}
                                                                min={0}
                                                                max={100}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    )}

                                    {/* Risk Factors */}
                                    {data.risk_factors && data.risk_factors.length > 0 && (
                                        <div>
                                            <h4 className="text-sm font-medium text-primary mb-3">
                                                {t('detail.actions.riskFactors', 'Active Risk Factors')}
                                            </h4>
                                            <div className="space-y-2">
                                                {data.risk_factors.map((factor, idx) => (
                                                    <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-secondary">
                                                        <AlertCircle className="h-4 w-4 text-fg-warning-primary shrink-0 mt-0.5" />
                                                        <div className="flex-1">
                                                            <p className="text-sm font-medium text-primary">{factor.title || factor.type}</p>
                                                            <p className="text-xs text-tertiary capitalize">
                                                                {factor.type?.replace(/_/g, ' ')} - {factor.severity} severity
                                                            </p>
                                                        </div>
                                                        <Badge color={factor.severity === 'critical' ? 'error' : factor.severity === 'high' ? 'error' : 'warning'} size="sm">
                                                            +{((factor.weight || 0) * 100).toFixed(0)}%
                                                        </Badge>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Recommendations */}
                                    {data.recommendations && data.recommendations.length > 0 && (
                                        <div>
                                            <h4 className="text-sm font-medium text-primary mb-3">
                                                {t('detail.actions.recommendations', 'Recommendations')}
                                            </h4>
                                            <ul className="space-y-2">
                                                {data.recommendations.map((rec, idx) => (
                                                    <li key={idx} className="flex items-start gap-2 text-sm text-secondary">
                                                        <CheckCircle className="h-4 w-4 text-fg-brand-primary shrink-0 mt-0.5" />
                                                        {rec}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {/* Historical Comparison */}
                                    {data.historical_comparison && data.historical_comparison.percentile != null && (
                                        <div className="p-3 rounded-lg bg-secondary text-center">
                                            <p className="text-sm text-tertiary">
                                                {t('detail.actions.historicalComparison', 'This shipment is in the {{percentile}}th percentile for risk (average: {{avg}}%)', {
                                                    percentile: data.historical_comparison.percentile,
                                                    avg: ((data.historical_comparison.average_risk ?? 0) * 100).toFixed(0),
                                                })}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-border-primary">
                            {data && (
                                <Button
                                    color="secondary"
                                    onClick={handleDownloadPDF}
                                    isLoading={isDownloading}
                                    iconLeading={Download01}
                                >
                                    {t('detail.actions.downloadPDF', 'Download PDF')}
                                </Button>
                            )}
                            <Button color="secondary" onClick={onClose}>
                                {t('common:close', 'Close')}
                            </Button>
                        </div>
                    </div>
                </Dialog>
            </Modal>
        </ModalOverlay>
    );
}
